// TranscriptionService.js
import { WebsocketManager } from './WebSocketManager';
import { AudioProcessor } from './AudioProcessor';
import store from "../../../store/store";
import {
  transcriptionStarted,
  transcriptionStopped,
  setTranscriptionState,
} from "../store/TranscriptionSlice/TranscriptionSlice";
import { resolveLanguageCatalogState } from "./LanguageCatalogState";

class TranscriptionService {
  constructor(wsUrl, userDetails, isMicActive) {
    this.wsUrl = wsUrl;
    this.userDetails = userDetails;
    this.TSStatus = "inactive"; 
    this.isOwner = false;
    this.room = null;
    this.livekitRoom = null;
    this.isMicActive = isMicActive;

    this.wsManager = new WebsocketManager(
      `${this.wsUrl}/${this.userDetails?.email}/${this.userDetails?.name?.replace(/ /g, "_")}`
    );
    this.audioProcessor = new AudioProcessor(null);
  }

  setTSStatus(status) {
    this.TSStatus = status;
    store.dispatch(setTranscriptionState({ TSstatus: status }));
  }


  subscribeToWebscoketEvents() {
  this.wsManager.on("connected", () => {
    if (this.room !== null) {
      setTimeout(() => {
        this.wsManager.emit("room_changed", { room_id: this.room });
      }, 500);
    }
    if (this.wsManager?.isConnected) {
      this.wsManager.emit("get_stt_languages", {});
    }
    this.getState();
  });

  this.wsManager.on("disconnected", () => {
    this.resetState();
  });

  this.wsManager.on("transcription_started", () => {
    store.dispatch(transcriptionStarted());
    store.dispatch(
      setTranscriptionState({
        transcription: "",
        translation: "",
        detectedLang: null,
      })
    );
  });

  this.wsManager.on("transcription_stopped", () => {
    this.TSStatus = "inactive";
    store.dispatch(transcriptionStopped());
    store.dispatch(
      setTranscriptionState({
        transcription: "",
        translation: "",
        detectedLang: null,
      })
    );
  });

  this.wsManager.on("stt_languages", (data) => {
    const cur = store.getState().transcription;
    store.dispatch(setTranscriptionState(resolveLanguageCatalogState(data, cur)));
  });

  this.wsManager.on("transcript_updated", (data) => {
    store.dispatch(setTranscriptionState({ transcription: data.transcript }));
  });

  this.wsManager.on("translation_updated", (data) => {
    const cur = store.getState().transcription;
    const incomingTargetLang = data.target_lang ?? data.targetLang;
    if (
      incomingTargetLang &&
      cur.translationTargetLang &&
      incomingTargetLang !== cur.translationTargetLang
    ) {
      return;
    }
    store.dispatch(setTranscriptionState({
      translation: data.translation,
      detectedLang: data.detected_lang ?? data.detectedLang,
      targetLang: incomingTargetLang ?? cur.translationTargetLang,
    }));
  });

  this.wsManager.on("update_state", (data) => {
    if (data.room_id !== this.room) {
      this.wsManager.emit("room_changed", { room_id: this.room });
    } else {
      const cur = store.getState().transcription;
      const newStatus = data.is_meeting ? "active" : "inactive";
      const prevStatus = cur.TSstatus;
      // Keep local status in sync
      this.TSStatus = newStatus;
      this.isOwner = !!data.is_owner;
      const payload = {
        TSstatus: newStatus,
        isOwner: data.is_owner,
        owner: data.owner,
        transcription: data.transcription || "",
      };
      if (data.stt_source_lang != null && data.stt_source_lang !== "") {
        payload.sttSourceLang = data.stt_source_lang;
      }
      if (data.translation_target_lang != null && data.translation_target_lang !== "") {
        payload.translationTargetLang = cur.translationTargetLang || data.translation_target_lang;
        payload.targetLang = cur.translationTargetLang || data.translation_target_lang;
      }
      if (!data.is_meeting) {
        payload.translation = "";
        payload.detectedLang = null;
      }
      store.dispatch(setTranscriptionState(payload));
      if (prevStatus !== newStatus) {
     if (newStatus === "active") {
       store.dispatch(transcriptionStarted());
     } else if (newStatus === "inactive") {
       store.dispatch(transcriptionStopped());
     }
   }
      this.mode = data.mode || this.mode;
      // If we're active and mic is on, start streaming audio in client mode
      this.updateMicState(this.isMicActive);
    }
  });

  this.wsManager.on("room_changed_server", (data) => {
    if (!(data.success && data.room_id === this.room)) {
      this.wsManager.emit("room_changed", { room_id: this.room });
    }
    this.getState();
    if (this.wsManager?.isConnected) {
      this.wsManager.emit("get_stt_languages", {});
    }
  });
  
  // If the WebSocket is already connected when subscribing, run the
  // initialization actions immediately to avoid a race where the
  // 'connected' event fired before listeners were attached.
  if (this.wsManager?.isConnected) {
    // Re-emit room_changed if we already have a room set so the server
    // can attach this client to the proper meeting context.
    if (this.room !== null) {
      setTimeout(() => {
        this.wsManager.emit("room_changed", { room_id: this.room });
      }, 200);
    }
    // Request languages and current state immediately
    this.wsManager.emit("get_stt_languages", {});
    this.getState();
  }
}



  // Public Methods


   getState() {
    if (!this.wsManager?.isConnected) return;
    setTimeout(() => {
      this.wsManager.emit("get_state", null);
    }, 1500);
  }

  /** Redemande le catalogue de langues au serveur (réponse attendue : `stt_languages`). */
  requestSttLanguages() {
    if (!this.wsManager?.isConnected) return;
    this.wsManager.emit("get_stt_languages", {});
  }

   activateTranscription() {
    if (this.TSStatus === "active") return;
    store.dispatch(
      setTranscriptionState({
        transcription: "",
        translation: "",
        detectedLang: null,
      })
    );
    this.setTSStatus("pending");

    if (this.wsManager.isConnected) {
      const { sttSourceLang, translationTargetLang } = store.getState().transcription;
      const startPayload = {};
      if (sttSourceLang) startPayload.stt_source_lang = sttSourceLang;
      if (translationTargetLang) startPayload.translation_target_lang = translationTargetLang;
      const hasLangKeys = Object.keys(startPayload).length > 0;
      this.wsManager.emit("transcription_start", hasLangKeys ? startPayload : null);
    }
    setTimeout(() => {
      this.getState();
    }, 2500);
  }




  emitTranscriptionLang(sttSourceLang, translationTargetLang) {
    if (!this.wsManager?.isConnected) return;
    this.wsManager.emit("transcription_lang", {
      stt_source_lang: sttSourceLang,
      translation_target_lang: translationTargetLang,
    });
  }




  stopTranscription() {
    if (!this.wsManager?.isConnected) return;
    this.TSStatus = "inactive";
    this.audioProcessor.stop();
    this.wsManager.emit("transcription_end", null);
    store.dispatch(
      setTranscriptionState({
        transcription: "",
        translation: "",
        detectedLang: null,
      })
    );
  }

  updateMicState(isMicActive) {
    this.isMicActive = isMicActive;
    //console.log("is MicActive?" ,this.isMicActive , "TSStatus?" ,this.TSStatus )
    const ws = this.wsManager?.ws;
    const wsOpen = ws && ws.readyState === WebSocket.OPEN;
    const STREAMING_MODES = ["client", "kyutai", "voxtral", "whisper", "canary"];
    if (this.isMicActive && this.TSStatus === 'active' && STREAMING_MODES.includes(this.mode) && wsOpen) {
      this.audioProcessor.start(ws);
    } else {
      this.audioProcessor.stop();
    }
  }

  getDetailedCallerInfo() {
    try {
        const stack = new Error().stack;

        if (stack) {
            // Split the stack trace into lines
            const stackLines = stack.split('\n');

            // Skip the first line (Error) and the second line (current function)
            const callerLines = stackLines.slice(2);

            // Find the first meaningful caller line
            const detailedCallerInfo = callerLines.map((line, index) => {
                // Try to extract as much information as possible
                const trimmedLine = line.trim();

                // Detailed parsing of the stack trace line
                const fileMatch = trimmedLine.match(/\((.*):(\d+):(\d+)\)$/);
                const atMatch = trimmedLine.match(/at\s+(.+)\s*\((.*):(\d+):(\d+)\)/);
                const simpleAtMatch = trimmedLine.match(/at\s+(.+)\s*$/);

                return {
                    fullLine: trimmedLine,
                    functionName: (atMatch?.[1] || simpleAtMatch?.[1] || 'unknown'),
                    fileName: (fileMatch?.[1] || atMatch?.[2] || 'unknown'),
                    lineNumber: (fileMatch?.[2] || atMatch?.[3] || 'unknown'),
                    columnNumber: (fileMatch?.[3] || atMatch?.[4] || 'unknown'),
                    isAnonymous: trimmedLine.includes('<anonymous>'),
                    stackIndex: index
                };
            }).filter(info =>
                info.functionName !== 'unknown' &&
                !info.fullLine.includes('getDetailedCallerInfo')
            );

            return detailedCallerInfo[0] || null;
        }
    } catch (error) {
        console.error('Error getting detailed caller info:', error);
    }

    return null;
}



logState(){
  //console.log('Room:', this.room);
  //console.log('TSStatus:', this.TSStatus);
  //console.log('isOwner:', this.isOwner);
}


  resetState() {
    this.TSStatus = "inactive";
    this.audioProcessor.stop();
    store.dispatch(setTranscriptionState({
      isOwner: false,
      owner: null,
      TSstatus: "inactive",
      transcription: "",
      translation: "",
      detectedLang: null,
    }));
  }

  setRoom(room) {
    if (this.room !== room) {
      this.room = room;
      this.resetState();
      if (this.wsManager?.isConnected) {
        this.wsManager.emit("room_changed", { room_id: this.room });
      }
      this.audioProcessor.stop();
    }
  }

  setLivekitRoom(room) {
    this.livekitRoom = room;
    this.audioProcessor.room = room;
  }




  startAudioProcessing() {
    if (this.isMicActive && this.TSStatus === 'active') {
      this.audioProcessor.start(this.normalSocket.socket);
    }
  }

  stopAudioProcessing() {
    this.audioProcessor.stop();
  }

  restartAudioProcessing() {
    this.audioProcessor.stop();
    this.audioProcessor.start(this.normalSocket.socket);
  }

  /**
   * Ferme la WebSocket (sans reconnexion), arrête l’audio et vide le store.
   * Utilisé au changement d’URL STT / utilisateur ou au démontage du composant.
   */
  destroy() {
    this.audioProcessor.stop();
    if (this.wsManager) {
      this.wsManager.destroy();
      this.wsManager = null;
    }
    this.room = null;
    this.livekitRoom = null;
    this.resetState();
  }
}


function transcriptionConnectionKey(wsUrl, userDetails) {
  if (!wsUrl || !userDetails?.email) return null;
  return `${wsUrl}|${userDetails.email}|${String(userDetails.name ?? "")}`;
}

let instance = null;

/**
 * Service singleton de transcription temps réel.
 * Sans argument : retourne l’instance courante (ex. MediaControlPanel).
 * Avec wsUrl + userDetails : crée ou remplace l’instance si la clé de connexion change.
 */
export function getTransciptionService(wsUrl, userDetails, isMicActive) {
  if (!wsUrl && instance) return instance;
  if (!wsUrl && !instance) return null;

  const key = transcriptionConnectionKey(wsUrl, userDetails);
  if (!key) return instance;

  if (instance && instance._connectionKey !== key) {
    store.dispatch(setTranscriptionState({
      sttLanguageOptions: [],
      translationLanguageOptions: [],
    }));
    instance.destroy();
    instance = null;
  }

  if (instance === null) {
    instance = new TranscriptionService(wsUrl, userDetails, isMicActive);
    instance._connectionKey = key;
  } else if (typeof isMicActive === "boolean") {
    instance.isMicActive = isMicActive;
  }
  return instance;
}

export function destroyTranscriptionService() {
  if (instance) {
    instance.destroy();
    instance = null;
  }
}
